import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { message } from "antd";
import axios from "axios";
import "./AdminMonthlySpend.css";

const money = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0";
  return x.toLocaleString("en-IN");
};

const AdminMonthlySpend = () => {
  const [loading, setLoading] = useState(false);
  const [savingPrize, setSavingPrize] = useState(false);
  const [closingMonth, setClosingMonth] = useState(false);

  const [data, setData] = useState(null);
  const [errorBox, setErrorBox] = useState("");

  const [prize1, setPrize1] = useState("");
  const [prize2, setPrize2] = useState("");
  const [prize3, setPrize3] = useState("");

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: "Bearer " + token } : {};
  };

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setErrorBox("");

      const res = await axios.get("/api/admin/monthly-spend?limit=20", {
        headers: authHeaders(),
      });

      if (!res.data?.success) {
        const msg = res.data?.message || "Failed to fetch leaderboard";
        setErrorBox(msg);
        message.error(msg);
        return;
      }

      const payload = res.data?.data || null;
      setData(payload);

      const cur = payload?.prizes?.current || {};
      setPrize1(cur?.first || "");
      setPrize2(cur?.second || "");
      setPrize3(cur?.third || "");
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || err?.message || "Failed to fetch leaderboard";
      setErrorBox(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
    // eslint-disable-next-line
  }, []);

  const currentMonth = data?.months?.currentMonth || "";
  const lastMonth = data?.months?.lastMonth || "";

  const currentTop = useMemo(() => data?.current?.topUsers || [], [data]);
  const lastTop = useMemo(() => data?.last?.topUsers || [], [data]);

  const savePrizes = async () => {
    try {
      const first = String(prize1 || "").trim();
      const second = String(prize2 || "").trim();
      const third = String(prize3 || "").trim();

      if (!first && !second && !third) {
        return message.error("Please enter at least one prize");
      }

      setSavingPrize(true);
      setErrorBox("");

      const res = await axios.post(
        "/api/admin/monthly-spend/prizes",
        { monthType: "current", first, second, third },
        { headers: authHeaders() }
      );

      if (res.data?.success) {
        message.success("Prizes saved");
        await fetchLeaderboard();
      } else {
        const msg = res.data?.message || "Failed to save prizes";
        setErrorBox(msg);
        message.error(msg);
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || "Failed to save prizes";
      setErrorBox(msg);
      message.error(msg);
    } finally {
      setSavingPrize(false);
    }
  };

  const closeMonth = async () => {
    try {
      const ok = window.confirm(
        "Close month will snapshot current top spenders and move current prizes to last month. Continue?"
      );
      if (!ok) return;

      setClosingMonth(true);
      setErrorBox("");

      const res = await axios.post(
        "/api/admin/monthly-spend/close-month",
        {},
        { headers: authHeaders() }
      );

      if (res.data?.success) {
        message.success("Month closed successfully");
        await fetchLeaderboard();
      } else {
        const msg = res.data?.message || "Failed to close month";
        setErrorBox(msg);
        message.error(msg);
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || "Failed to close month";
      setErrorBox(msg);
      message.error(msg);
    } finally {
      setClosingMonth(false);
    }
  };

  return (
    <AdminLayout>
      <div className="msWrap msThemeFix">
        {/* HEADER */}
        <div className="msHead">
<div className="msHeading">
  <h3 className="msHeading__title">Monthly Top Spenders</h3>

  <div className="msHeading__meta">
    <span className="msHeading__label">Current:</span>
    <b className="msHeading__value">{currentMonth || "-"}</b>

    <span className="msHeading__sep">|</span>

    <span className="msHeading__label">Last:</span>
    <b className="msHeading__value">{lastMonth || "-"}</b>
  </div>
</div>

          <div className="msActions">
            <button
              type="button"
              className="msBtn outline"
              onClick={fetchLeaderboard}
              disabled={loading || savingPrize || closingMonth}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>

            <button
              type="button"
              className="msBtn danger"
              onClick={closeMonth}
              disabled={loading || savingPrize || closingMonth}
              title="Snapshot current month & move to last month"
            >
              {closingMonth ? "Closing..." : "Close month"}
            </button>
          </div>
        </div>

        {/* ERROR BOX */}
        {errorBox ? (
          <div className="msErrorBox">
            <div className="msErrorTitle">API Error</div>
            <div className="msErrorMsg">{errorBox}</div>
            <div className="msErrorHint">
              Check route path, admin token, and backend logs.
            </div>
          </div>
        ) : null}

        {/* PRIZE CARD */}
        <div className="msCard">
          <div className="msCardTitle">Rewards (Current Month)</div>

          <div className="msPrizeGrid">
            <div className="msField">
              <label>1st Prize</label>
              <input
                value={prize1}
                onChange={(e) => setPrize1(e.target.value)}
                placeholder="e.g. ₹500 wallet / Gift / Coupon"
              />
            </div>

            <div className="msField">
              <label>2nd Prize</label>
              <input
                value={prize2}
                onChange={(e) => setPrize2(e.target.value)}
                placeholder="e.g. ₹300 wallet"
              />
            </div>

            <div className="msField">
              <label>3rd Prize</label>
              <input
                value={prize3}
                onChange={(e) => setPrize3(e.target.value)}
                placeholder="e.g. ₹100 wallet"
              />
            </div>
          </div>

          <div className="msPrizeBtns">
            <button
              type="button"
              className="msBtn"
              onClick={savePrizes}
              disabled={loading || savingPrize || closingMonth}
            >
              {savingPrize ? "Saving..." : "Save prizes"}
            </button>

            <div className="msHint">
              Tip: Use <b>Close month</b> at month end to freeze winners & move prizes.
            </div>
          </div>
        </div>

        {/* TABLES + MOBILE CARDS */}
        <div className="msGrid2">
          {/* CURRENT MONTH */}
          <div className="msCard">
            <div className="msCardTitle">
              Current Month Top 20 <span className="msTag live">LIVE</span>
            </div>

            {/* Desktop table */}
            <div className="msDesktopOnly">
              <div className="msTableScroll">
                <table className="msTable">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User</th>
                      <th>Email</th>
                      <th>Orders</th>
                      <th className="msRight">Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTop.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="msEmpty">
                          {loading ? "Loading..." : "No data"}
                        </td>
                      </tr>
                    ) : (
                      currentTop.map((u) => (
                        <tr key={u.email}>
                          <td>{u.rank}</td>
                          <td>{u.fname || "-"}</td>
                          <td className="msMono">{u.email}</td>
                          <td>{u.orders || 0}</td>
                          <td className="msRight">₹ {money(u.spend)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="msMobileOnly">
              {currentTop.length === 0 ? (
                <div className="msEmptyCard">
                  {loading ? "Loading..." : "No data"}
                </div>
              ) : (
                <div className="msCards">
                  {currentTop.map((u) => (
                    <div className="msUserCard" key={u.email}>
                      <div className="msUserCardTop">
                        <div className="msRankBadge">#{u.rank}</div>
                        <div className="msUserInfo">
                          <div className="msUserName">{u.fname || "-"}</div>
                          <div className="msUserEmail msMono">{u.email}</div>
                        </div>
                      </div>

                      <div className="msUserCardBottom">
                        <div className="msMiniStat">
                          <div className="msMiniLabel">Orders</div>
                          <div className="msMiniValue">{u.orders || 0}</div>
                        </div>
                        <div className="msMiniStat right">
                          <div className="msMiniLabel">Spend</div>
                          <div className="msMiniValue spend">₹ {money(u.spend)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="msFooterNote">
              Winners (Top 10) are based on total <b>successful paid</b> spend for this month.
            </div>
          </div>

          {/* LAST MONTH */}
          <div className="msCard">
            <div className="msCardTitle">
              Last Month Top 20 <span className="msTag snap">SNAPSHOT</span>
            </div>

            {/* Desktop table */}
            <div className="msDesktopOnly">
              <div className="msTableScroll">
                <table className="msTable">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User</th>
                      <th>Email</th>
                      <th>Orders</th>
                      <th className="msRight">Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastTop.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="msEmpty">
                          {loading ? "Loading..." : "No snapshot found (close month first)"}
                        </td>
                      </tr>
                    ) : (
                      lastTop.map((u) => (
                        <tr key={u.email}>
                          <td>{u.rank}</td>
                          <td>{u.fname || "-"}</td>
                          <td className="msMono">{u.email}</td>
                          <td>{u.orders || 0}</td>
                          <td className="msRight">₹ {money(u.spend)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="msMobileOnly">
              {lastTop.length === 0 ? (
                <div className="msEmptyCard">
                  {loading ? "Loading..." : "No snapshot found (close month first)"}
                </div>
              ) : (
                <div className="msCards">
                  {lastTop.map((u) => (
                    <div className="msUserCard" key={u.email}>
                      <div className="msUserCardTop">
                        <div className="msRankBadge">#{u.rank}</div>
                        <div className="msUserInfo">
                          <div className="msUserName">{u.fname || "-"}</div>
                          <div className="msUserEmail msMono">{u.email}</div>
                        </div>
                      </div>

                      <div className="msUserCardBottom">
                        <div className="msMiniStat">
                          <div className="msMiniLabel">Orders</div>
                          <div className="msMiniValue">{u.orders || 0}</div>
                        </div>
                        <div className="msMiniStat right">
                          <div className="msMiniLabel">Spend</div>
                          <div className="msMiniValue spend">₹ {money(u.spend)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="msFooterNote">
              Snapshot is frozen when you click <b>Close month</b>.
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMonthlySpend;